import type Alpine from "alpinejs"

const DEFAULT_DEPTH = 3000
const DEFAULT_PERSPECTIVE = 120

/**
 * Registers the `x-tilt` Alpine directive.
 *
 * Applies a 3D look-at transform: the element's surface normal always points
 * toward the cursor, as if a virtual point sits at depth D in front of the
 * screen. Mathematically this is the atan2 decomposition of the look-at vector
 * (dx, dy, D) into separate rotateX / rotateY angles.
 *
 * @example
 * <div x-tilt>...</div>        uses DEFAULT_DEPTH
 * <div x-tilt="800">...</div>  custom depth — smaller = more sensitive tilt
 */
export function defineTiltDirective(alpine: typeof Alpine) {
  alpine.directive("tilt", (el, { expression }, { cleanup }) => {
    const depth = expression ? parseFloat(expression) : DEFAULT_DEPTH

    let rect = el.getBoundingClientRect()
    const onResize = () => {
      rect = el.getBoundingClientRect()
    }

    const handler = (event: MouseEvent) => {
      const dx = event.clientX - (rect.left + rect.width / 2)
      const dy = event.clientY - (rect.top + rect.height / 2)
      const toDeg = 180 / Math.PI
      const ry = Math.atan2(dx, Math.sqrt(dy * dy + depth * depth)) * toDeg
      const rx = Math.atan2(-dy, depth) * toDeg
      el.style.transform = `perspective(${DEFAULT_PERSPECTIVE}px) rotateX(${rx}deg) rotateY(${ry}deg)`
    }

    window.addEventListener("mousemove", handler, { passive: true })
    window.addEventListener("resize", onResize, { passive: true })
    cleanup(() => {
      window.removeEventListener("mousemove", handler)
      window.removeEventListener("resize", onResize)
    })
  })
}
